import type { SelectedService } from "@/types/service";
import type {
  ImpactAnalysisCalculatedResult,
  ImpactAnalysisData,
  ImpactLevel,
} from "@/types/impact-analysis";
import type {
  ExtractedFileData,
  NotificationTransformResult,
  RomanianStateRelationshipData,
  EUServiceProvisionData,
  Article9Data,
} from "../types";
import { NOTIFICATION_SERVICE_ROWS } from "../constants";

function isDaValue(field: { parsedValue: unknown } | undefined): boolean {
  return (
    typeof field?.parsedValue === "string" &&
    field.parsedValue.toLowerCase() === "da"
  );
}

function extractSelectedServices(
  extracted: ExtractedFileData
): SelectedService[] {
  if (isDaValue(extracted.fields.service_row_none)) return [];

  const services: SelectedService[] = [];
  for (const row of NOTIFICATION_SERVICE_ROWS) {
    const field = extracted.fields[`service_row_${row.row}`];
    if (isDaValue(field)) {
      services.push({
        entity_id: row.entityId,
        category_id: row.categoryId,
        sector_name: row.sectorName,
        subsector_name: row.subsectorName,
        name: row.label,
      });
    }
  }
  return services;
}

export function transformNotification(
  extracted: ExtractedFileData
): NotificationTransformResult {
  const val = (fieldName: string) =>
    extracted.fields[fieldName]?.parsedValue ?? null;
  const str = (fieldName: string) => val(fieldName) as string | null;
  const num = (fieldName: string) => val(fieldName) as number | null;

  const selectedServices = extractSelectedServices(extracted);
  const article9Result = extractArticle9Result(extracted);

  const romanianStateRelationship: RomanianStateRelationshipData = {
    stabilita_romania: isDaValue(extracted.fields.stabilita_romania),
    sediu_principal_romania: isDaValue(
      extracted.fields.sediu_principal_romania
    ),
  };

  const euServiceProvision: EUServiceProvisionData = {
    furnizare_servicii_romania: isDaValue(
      extracted.fields.furnizare_servicii_romania
    ),
    furnizare_servicii_ue: isDaValue(extracted.fields.furnizare_servicii_ue),
  };

  const article9: Article9Data = {
    isSoleProvider: isDaValue(extracted.fields.art_9a),
    publicImpactLevel: parseRiskLevel(str("art_9b")),
    systemicRiskLevel: parseRiskLevel(str("art_9c")),
    isNationalCritical: isDaValue(extracted.fields.art_9d),
  };

  const evaluarePreliminaraData = str("evaluare_preliminara");

  return {
    romanianStateRelationship,
    euServiceProvision,
    article9,
    evaluarePreliminaraData,
    entityData: {
      ...(str("denumire_entitate") && {
        nume_entitate: str("denumire_entitate")!,
      }),
      ...(str("cui") && { cui: str("cui")! }),
      ...(str("cif") && { cif: str("cif")! }),
      ...(str("email") && { email: str("email")! }),
      ...(str("website") && { website: str("website")! }),
      ...((str("telefon_mobil") || str("telefon_fix")) && {
        telefon: (str("telefon_mobil") || str("telefon_fix"))!,
      }),
      ...(str("cod_postal") && { adresa: buildAddress(extracted) }),
      ...(str("caen_principal") && {
        caen_principal: { cod: str("caen_principal")!, denumire: "" },
      }),
      ...(str("caen_secundare") && {
        caen_secundar: str("caen_secundare")!
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean),
      }),
      ...(num("nr_salariati") !== null && {
        nr_salariati: num("nr_salariati")!,
      }),
      ...(num("cifra_afaceri") !== null && {
        cifra_afaceri: num("cifra_afaceri")!,
      }),
      ...(num("active_totale") !== null && { active: num("active_totale")! }),
      ...(str("dimensiune") && {
        dimensiune: mapDimensiune(str("dimensiune")!),
      }),
      selected_services: selectedServices.length > 0 ? selectedServices : null,
      ...(article9Result && {
        impact_analysis: { result: article9Result } as ImpactAnalysisData,
      }),
      relatie_stat_roman: {
        stabilita_in_romania: isDaValue(extracted.fields.stabilita_romania),
        sediu_principal_in_romania: isDaValue(
          extracted.fields.sediu_principal_romania
        ),
        entitate_administratie_publica: isDaValue(
          extracted.fields.entitate_administratie_publica
        ),
        entitate_critica_294_2024: isDaValue(
          extracted.fields.entitate_critica_294_2024
        ),
        entitate_extra_ue: isDaValue(extracted.fields.entitate_extra_ue),
        reprezentant_romania: {
          denumire: str("reprezentant_romania_denumire") ?? "",
          adresa: str("reprezentant_romania_adresa") ?? "",
          email: str("reprezentant_romania_email") ?? "",
          telefon: str("reprezentant_romania_telefon") ?? "",
        },
      },
      situatie_servicii_ue: {
        furnizeaza_servicii_romania: isDaValue(
          extracted.fields.furnizare_servicii_romania
        ),
        furnizeaza_servicii_ue: isDaValue(
          extracted.fields.furnizare_servicii_ue
        ),
        state_membre_ue: str("furnizare_servicii_ue_state_membre"),
      },
    },
    contactData: {
      ...(str("rep_legal_nume") && {
        reprezentantLegal: {
          nume: str("rep_legal_nume") ?? "",
          prenume: str("rep_legal_prenume") ?? "",
          email: str("contact_email") ?? "",
          telefon: str("contact_telefon_mobil") ?? "",
          functie: str("rep_legal_functia") ?? "",
        },
      }),
      ...(str("securitate_nume") && {
        responsabilSecuritate: {
          nume: str("securitate_nume") ?? "",
          prenume: str("securitate_prenume") ?? "",
          email: str("securitate_email") ?? "",
          telefon: str("securitate_telefon") ?? "",
          functie: str("securitate_functia") ?? "",
        },
      }),
      ...{
        contactOperational:
          str("contact_aceeasi_persoana")?.toLowerCase() === "da"
            ? {
                nume: str("securitate_nume") ?? "",
                prenume: str("securitate_prenume") ?? "",
                email: str("securitate_email") ?? "",
                telefon: str("securitate_telefon") ?? "",
                functie: str("securitate_functia") ?? "",
              }
            : {
                nume: str("contact_nume") ?? "",
                prenume: str("contact_prenume") ?? "",
                email: str("contact_email") ?? "",
                telefon: str("contact_telefon_mobil") ?? "",
                functie: str("contact_functia") ?? "",
              },
      },
      ...(str("ip_publice") && {
        adreseIP: str("ip_publice")!
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean),
      }),
    },
  };
}

function buildAddress(extracted: ExtractedFileData): string {
  const val = (f: string) => extracted.fields[f]?.parsedValue as string | null;
  const parts = [
    val("strada"),
    val("numar") ? `Nr. ${val("numar")}` : null,
    val("bloc") ? `Bl. ${val("bloc")}` : null,
    val("localitate"),
    val("judet"),
    val("cod_postal"),
  ].filter(Boolean);
  return parts.join(", ");
}

function mapDimensiune(value: string): "MICĂ" | "MIJLOCIE" | "MARE" | null {
  const lower = value.toLowerCase();
  if (lower.includes("mare")) return "MARE";
  if (lower.includes("mijloc")) return "MIJLOCIE";
  if (lower.includes("mic")) return "MICĂ";
  return null;
}

function parseBooleanField(value: string | null): boolean | null {
  if (!value) return null;
  const lower = value.toLowerCase().trim();
  if (lower === "da") return true;
  if (lower === "nu") return false;
  return null;
}

function parseImpactLevel(value: string | null): ImpactLevel | null {
  if (!value) return null;
  const lower = value.toLowerCase().trim();
  if (lower.includes("ridicat")) return "RIDICAT";
  if (lower.includes("mediu")) return "MEDIU";
  if (lower.includes("sc")) return "SCĂZUT";
  return null;
}

function extractArticle9Result(
  extracted: ExtractedFileData
): ImpactAnalysisCalculatedResult | null {
  const str = (f: string) =>
    (extracted.fields[f]?.parsedValue as string | null) ?? null;

  const article9a = parseBooleanField(str("art_9a"));
  const article9b = parseImpactLevel(str("art_9b"));
  const article9c = parseImpactLevel(str("art_9c"));
  const article9d = parseBooleanField(str("art_9d"));

  const hasAny =
    article9a !== null ||
    article9b !== null ||
    article9c !== null ||
    article9d !== null;

  if (!hasAny) return null;

  return { article9a, article9b, article9c, article9d };
}
function parseRiskLevel(
  value: string | null
): "RIDICAT" | "MEDIU" | "SCAZUT" | null {
  if (!value) return null;
  const upper = value.toUpperCase().trim();
  if (upper.includes("RIDICAT")) return "RIDICAT";
  if (upper.includes("MEDIU")) return "MEDIU";
  if (upper.includes("SCAZUT") || upper.includes("SCĂZUT")) return "SCAZUT";
  return null;
}
